import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from '@app/app.hook'
import { selectActiveModules, selectStrings } from '@app/slices/slice.app'

import { MODULE_TABLE } from '@app/app.config'
import { TOASTIFY_DEFAULT_OPTIONS } from '@app/app.constants'
import { getErrorText } from '@app/app.method'
import { selectToken } from '@app/slices/slice.token'
import { useInitializeSidebarVisibility } from '@login/MutationProvider/initializeSidebarVisibility'
import { useRevalidateToken } from '@login/MutationProvider/revalidateToken'
import { useValidateAPIPath } from '@login/MutationProvider/validateAPIPath'
import { useValidateRoute } from '@login/MutationProvider/validateRoute'

import {
    CAREPATH_FORMIK_INITIAL_VALUES,
    CAREPATH_VALIDATION_SCHEMA,
    MODULE_VERSION
} from '@fmt/constants'
import {
    CarepathKeys,
    CarepathParams,
    GetCarepathResponse,
    UpdateProfilePictureCarepathResponse
} from '@fmt/type'
import { ImageDisplay } from '@profile/styles/ImageDisplay'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'

import { selectRouter } from '@app/app.store'
import { TokenData } from '@app/types/type.token'
import {
    useEditCarepathMutation,
    useGetCarepathMutation,
    useUpdateProfilePictureCarepathMutation
} from '@fmt/api'
import DashboardResponsiveHeader from '@fmt/components/DashboardResponsiveHeader'
import CarepathDateName from '@fmt/components/carepaths/details/inputs/DateName'
import CarepathName from '@fmt/components/carepaths/details/inputs/Name'
import { back, push } from '@lagunovsky/redux-react-router'

import LinkTreatmentFromCarepathDropdownEdit from '@fmt/components/carepaths/details/inputs/LinkTreatmentFromCarepathDropdownEdit'
import _ from 'lodash'
import { useParams } from 'react-router-dom'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'

const ProfilePic = ({
    showRecentlyUploaded,
    updateProfilePictureCarepathMutation,
    getCarepathMutation
}: {
    showRecentlyUploaded: boolean
    updateProfilePictureCarepathMutation: {
        data: UpdateProfilePictureCarepathResponse | undefined
    }
    getCarepathMutation: {
        data?: GetCarepathResponse | undefined
    }
}) => {
    const url = showRecentlyUploaded
        ? updateProfilePictureCarepathMutation.data?.data.url
        : getCarepathMutation.data?.data.profilePicture
    const imageThumbnail = url
        ? <ImageDisplay
            url={url}
            className={'rounded-square profile'}
        />
        : <div
            className={[
                'img-placeholder rounded-square profile'
            ].join(' ')}
        >
        </div>

    return imageThumbnail
}

const CarepathDetailsInterface = () => {
    const dispatch = useAppDispatch()
    const strings = useAppSelector(selectStrings)

    const activeModules = useAppSelector(selectActiveModules)

    const revalidateToken = useRevalidateToken()
    const token = useAppSelector(selectToken)
    const validateAPIPath = useValidateAPIPath()
    const router = useAppSelector(selectRouter)
    const validateRoute = useValidateRoute()
    const initializeSidebarVisibility = useInitializeSidebarVisibility()

    // upload profile picture logic.
    const [file, setFile] = useState<File | null>(null)
    const [getCarepath, getCarepathMutation] = useGetCarepathMutation()
    const [showRecentlyUploaded, setShowRecentlyUploaded] = useState<boolean>(false)
    const [showCancelWarning, setShowCancelWarning] = useState<boolean>(false)

    const { carepathId } = useParams<CarepathParams>()

    const [editCarepath, editCarepathMutation] = useEditCarepathMutation()

    const [mode, setMode] = useState<'EDIT' | 'VIEW'>(
        _.lowerCase(router.location.pathname).includes('edit') ? 'EDIT' : 'VIEW'
    )

    // a fix that does the ff things:
    // before this existed, the getPatients gets called twice from two separate useEffects
    // the first hook should only be done once you clicked on either buttons that
    // changes the url. ideally, a quick solution. should be set to FALSE.
    const [modeButtonClicked, setModeButtonClicked] = useState(false)

    const [isOutsideClickTreatment, setIsOutsideClickTreatment] = useState(false)

    const carepathEditFormik = useFormik({
        initialValues: getCarepathMutation.data?.data || CAREPATH_FORMIK_INITIAL_VALUES,
        validateOnChange: false,
        validateOnBlur: false,
        validationSchema: CAREPATH_VALIDATION_SCHEMA(
            strings.app?.message.error.empty || ''
        ),
        onSubmit: (values) => {
            const call = async () => {
                if (token.valid) {
                    const newToken = await revalidateToken(
                        {
                            value: token.value,
                            id: token.id
                        },
                        token.mode
                    )
                    const foundApiPath = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths.editCarepath.path,
                        true
                    )

                    if (foundApiPath && newToken.value) {
                        editCarepath({
                            authToken: newToken.value,
                            data: {
                                carepathId: carepathId || '',
                                treatmentId: getCarepathMutation.data?.data.treatmentId || '',
                                carepathName: values.carepathName || '',
                                dateName: values.dateName || ''
                            }
                        })
                    }
                }
            }
            call()
        }
    })

    /** create fetch data function */
    const fetchData = (token: TokenData) => {
        let isMounted = true

        const call = async () => {
            if (
                token.valid &&
                carepathId
            ) {
                const newToken = await revalidateToken({
                    value: token.value,
                    id: token.id
                }, token.mode)
                if (isMounted) {
                    const isValid = validateAPIPath(
                        activeModules.arr,
                        MODULE_TABLE.fmt.moduleName,
                        MODULE_TABLE.fmt.apiPaths
                            .getCarepathId.path,
                        true
                    )

                    if (isValid && newToken.value) {
                        getCarepath({
                            authToken: newToken.value,
                            data: {
                                carepathId
                            }
                        })
                    }
                }
            }
        }

        call()

        return () => {
            isMounted = false
        }
    }

    useEffect(() => {
        initializeSidebarVisibility(true)
    }, [])

    // THIS FETCH SHOULD NOT HAPPEN ON MOUNT.
    // solution change dependency to a useState. a button where you clicked on it
    // and then unset it.
    useEffect(() => {
        // should only be performed IF the button is clicked.
        if (modeButtonClicked) {
            setMode(
                _.lowerCase(router.location.pathname).includes('edit') ? 'EDIT' : 'VIEW'
            )

            // you go here ideally after submitting the values from the edit mode.
            if (_.lowerCase(router.location.pathname).includes('view')) {
                carepathEditFormik.resetForm()
                // and then fetch the data again just to double check.
                // you only want to do this IF you came from clicking the button.
                fetchData(token)
            }

            // don't forget to set it false after.
            setModeButtonClicked(false)
        }
    }, [router.location.pathname, modeButtonClicked])

    // ONLY fetch it when obj.needAccordion is true AND if isSuccess is false.
    useEffect(() => {
        if (carepathId) {
            setShowRecentlyUploaded(false)
            return fetchData(token)
        } else {
            return () => {}
        }
    }, [token.id, token.valid, carepathId])

    useEffect(() => {
        if (getCarepathMutation.data) {
            const data = getCarepathMutation.data
            carepathEditFormik.setValues({
                ...carepathEditFormik.values,
                ...data.data
            })
        }
    }, [getCarepathMutation.data])

    useEffect(() => {
        if (getCarepathMutation.error) {
            const message = getErrorText(getCarepathMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [getCarepathMutation.error])

    useEffect(() => {
        const data = editCarepathMutation.data

        if (data) {
            if (data.status === 'OK') {
                // close component by clearing the edit formik.
                toast.success(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                // carepathEditFormik.resetForm()
                fetchData(token)
            } else {
                toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
            }
        }
    }, [editCarepathMutation.data])

    useEffect(() => {
        if (editCarepathMutation.error) {
            const message = getErrorText(editCarepathMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [editCarepathMutation.error])

    const [updateProfilePictureCarepath, updateProfilePictureCarepathMutation] =
    useUpdateProfilePictureCarepathMutation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const selectedFile = event.target.files?.[0]
        if (selectedFile) {
            setFile(selectedFile)
        }
    }

    const handleUpload = async () => {
        if (file) {
            const formData = new FormData()
            formData.append('profilePicture', file)

            const newToken = await revalidateToken({
                value: token.value,
                id: token.id
            }, token.mode)

            const foundApiPath = validateAPIPath(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.apiPaths.updateProfilePictureCarepath.path,
                true
            )

            if (foundApiPath) {
                updateProfilePictureCarepath({
                    carepathId: carepathId || '',
                    data: formData,
                    authToken: newToken.value
                }).unwrap().then((data) => {
                    if (data.status === 'OK') {
                        // toast success message.
                        toast.success(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                        // we need to use this token now so set it.
                        setShowRecentlyUploaded(true)
                    } else {
                        toast.error(data.message, { ...TOASTIFY_DEFAULT_OPTIONS })
                    }
                })
            }
        }
    }

    useEffect(() => {
        handleUpload()
    }, [file])

    useEffect(() => {
        if (updateProfilePictureCarepathMutation.error) {
            const message = getErrorText(updateProfilePictureCarepathMutation.error)
            console.error(message)
            toast.error(message, { ...TOASTIFY_DEFAULT_OPTIONS })
        }
    }, [updateProfilePictureCarepathMutation.error])

    const handleKeyDown = (
        e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
        fieldName?: CarepathKeys
    ) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            const nextInput = document.querySelector(`[name=${ fieldName }]`) as HTMLInputElement
            if (nextInput) {
                nextInput.focus()
                nextInput.select()
            } else {
                carepathEditFormik.handleSubmit()
            }
        }
    }

    const NameInput = useMemo(() => {
        return <CarepathName
            readOnly={mode === 'VIEW'}
            carepathEditFormik={carepathEditFormik}
            handleKeyDown={(e) => {
                handleKeyDown(e, 'carepathName')
            }}
        />
    }, [
        mode,
        strings,
        carepathEditFormik.values.carepathName,
        carepathEditFormik.errors.carepathName
    ])

    const DateNameInput = useMemo(() => {
        return <CarepathDateName
            readOnly={mode === 'VIEW'}
            carepathEditFormik={carepathEditFormik}
            handleKeyDown={(e) => {
                if (e.key === 'Enter') { carepathEditFormik.handleSubmit() }
            }}
        />
    }, [
        strings,
        carepathEditFormik.values.dateName,
        carepathEditFormik.errors.dateName
    ])

    const CancelButton = <button type={'button'}
        className={[
            'btn btn-secondary w-100 py-2'
        ].join(' ')}
        onClick={() => {
            // if you made changes to the formik, show a warning first via popup
            const obj = carepathEditFormik.touched

            interface FormikTouched {
                [key: string]: boolean;
              }

            function hasTrueValue (obj: FormikTouched) {
                for (const key in obj) {
                    if (obj[key] === true) {
                        return true
                    }
                }
                return false
            }

            const hasTrue = hasTrueValue(obj)
            if (hasTrue) {
                // show warning pls
                setShowCancelWarning(true)
            } else {
                // go back to view
                const isValid = validateRoute(
                    activeModules.arr,
                    MODULE_TABLE.fmt.moduleName,
                    MODULE_TABLE.fmt.routes.viewCarepath,
                    true
                )

                setModeButtonClicked(true)

                dispatch(push(
                    _.replace(isValid.route,
                        ':carepathId',
                        carepathId || ''
                    )
                ))
            }
        }}>
        {strings.app?.text.cancel}
    </button>

    const LoadingContent = (
        <div className={'spinner-container'}>
            <span className={'spinner-border spinner-border-sm'}></span>
            <span className={'ms-2'}>{
                strings.app?.text.loading || ''
            }</span>
        </div>
    )

    const SubmitButton = <button type={'button'}
        disabled={
        // step 1 shouldn't be disabled here because of formik.
            false
        // step 2 can be skipped entirely.
        }
        className={[
            'btn btn-primary w-100 fw-semibold py-2'
        ].join(' ')}
        onClick={() => {
            carepathEditFormik.handleSubmit()
        }}>
        { editCarepathMutation.isLoading
            ? LoadingContent
            : strings.app?.text.save}
    </button>

    const EditButton = <button type={'button'}
        className={[
            'btn btn-secondary w-100 py-2'
        ].join(' ')}
        onClick={() => {
            const isValid = validateRoute(
                activeModules.arr,
                MODULE_TABLE.fmt.moduleName,
                MODULE_TABLE.fmt.routes.editCarepath,
                true
            )

            setModeButtonClicked(true)

            dispatch(push(
                _.replace(isValid.route,
                    ':carepathId',
                    carepathId || ''
                )
            ))
        }}>
        {strings.fmt?.text.department.details.edit}
    </button>

    const ChangeProfilePictureButton = <>
        <input
            type={'file'}
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileChange}
        />
        <button type={'button'}
            className={[
                'btn btn-secondary w-100 mt-5'
            ].join(' ')}
            onClick={() => {
                if (fileInputRef.current) {
                    fileInputRef.current.click()
                }
            }}>
            { editCarepathMutation.isLoading
                ? LoadingContent
                : strings.fmt?.text.department.details.change}
        </button>
    </>

    /** display list of carepaths here with the data table template. */

    const result = <div className={'row my-8'}>
        <div className={'col-12 col-lg'}>
            <div className={'edit-interface'}>
                <h2 className={'mb-5 fw-semibold '} >{strings.fmt?.text.carepath.carepath}</h2>
                {/* group one */}
                <div className={'mb-6'}>
                    <h4 className={'mb-6'}>{strings.fmt?.text.facility.add.steps['3'].details}</h4>
                    {NameInput}
                    {DateNameInput}
                    <LinkTreatmentFromCarepathDropdownEdit
                        carepathId={carepathId}
                        isOutsideClick={isOutsideClickTreatment}
                        setIsOutsideClick={setIsOutsideClickTreatment}
                    />
                </div>

                {mode === 'EDIT' && <div className={'container button-container'}>
                    <div className={'row align-items-center mt-6'}>
                        <div className={'col-6 col-lg-4 col-md-6 offset-lg-4 offset-md-0'}>
                            {CancelButton}
                        </div>
                        <div className={'col-6 col-lg-4 col-md-6'}>
                            {SubmitButton}
                        </div>
                    </div>
                </div>}

                {mode === 'VIEW' && <div className={'container button-container'}>
                    <div className={'align-items-center justify-content-end mt-6 row'}>
                        <div className={'col-6 col-lg-4 col-md-6'}>
                            {EditButton}
                        </div>
                    </div>
                </div>}
            </div>
        </div>
        <div className={'col-12 col-lg-auto mt-6 mt-lg-0'}>
            <div className={'picture-interface'}>
                <h2 className={'mb-5 fw-semibold '} >{strings.fmt
                    ?.text.department.details.image}</h2>
                <div className={'text-center'}>

                    <div className={'img-wrapper mx-auto mb-2'}>

                        {<ProfilePic
                            showRecentlyUploaded={showRecentlyUploaded}
                            updateProfilePictureCarepathMutation={{
                                data: updateProfilePictureCarepathMutation.data
                            }}
                            getCarepathMutation={getCarepathMutation}
                        />}

                    </div>
                    {
                        mode === 'EDIT' && ChangeProfilePictureButton
                    }
                </div>
            </div>
        </div>

    </div>

    return <div className={'carepath-page'}>

        {/* dashboard responsive header */}
        <DashboardResponsiveHeader />

        <div className={'main-content'}>

            <div className={'container-fluid mx-auto'}>
                <div className={'row justify-content-between pt-16 pb-0 px-5'}>
                    <div className={'col-auto'}>
                        <a onClick={(e) => {
                            e.preventDefault()
                            dispatch(back())
                        }} className={'btn btn-round btn-sm-md'}>
                            <i
                                className={'fa-light fa-arrow-left'}
                                aria-hidden={'true'}>
                            </i>
                        </a>
                    </div>
                    <div className={'col-auto'}>
                        <h1 className={'my-0'}>{
                            getCarepathMutation.data?.data.carepathName
                        }</h1>
                    </div>
                    <div className={'col-auto'}>
                        <a href={'#'}
                            onClick={(e) => {
                                e.preventDefault()
                            }} className={[
                                'btn btn-round btn-sm-md invisible'
                            ].join(' ')}>
                            <i
                                className={'fa-light fa-arrow-right'}
                                aria-hidden={'true'}
                            >
                            </i>
                        </a>
                    </div>
                </div>
                <div className={'row justify-content-between mt-5'}>
                    <div className={'col'}>
                        <div className={
                            'container-fluid px-5 px-sm-3 carepath-body'
                        }>
                            <div className={'h-100 row'}>
                                <div className={' col-12 mx-auto'}>
                                    {result}
                                </div>
                            </div>
                        </div>

                        <Modal centered={true} isOpen={showCancelWarning} toggle={() => {
                            setShowCancelWarning(!showCancelWarning)
                        }}>
                            <ModalHeader className={'justify-content-between'} toggle={() => {
                                setShowCancelWarning(!showCancelWarning)
                            }} close={ <a className={'btn btn-round '}
                                onClick={(e) => {
                                    e.preventDefault()
                                    setShowCancelWarning(false)
                                }}>
                                <i className={'fa-light fa-multiply'} aria-hidden={'true'}>
                                </i>
                            </a>}
                            >
                                <span className={'h5'}>{
                                    strings.fmt?.message?.data_edit_warning?.title || ''
                                }</span>
                            </ModalHeader>
                            <ModalBody>
                                {/* content changes if idle / expire sessions expired. */}
                                <small className={'d-block my-4'}>
                                    {strings.fmt?.message?.data_edit_warning?.content || ''}
                                </small>
                                <div className={'row justify-content-end'}>
                                    <div className={'col-auto mb-2 mb-md-0'}>
                                        <button
                                            type={'button'}
                                            className={'btn btn-primary w-100'}
                                            onClick={ () => {
                                                setShowCancelWarning(false)
                                            }}>
                                            {strings.app?.text?.no || ''}
                                        </button>
                                    </div>
                                    <div className={'col-auto'}>
                                        <button type={'button'} onClick={() => {
                                            const isValid = validateRoute(
                                                activeModules.arr,
                                                MODULE_TABLE.fmt.moduleName,
                                                MODULE_TABLE.fmt
                                                    .routes.viewCarepath,
                                                true
                                            )

                                            setModeButtonClicked(true)

                                            dispatch(push(
                                                _.replace(isValid.route,
                                                    ':carepathId',
                                                    carepathId || ''
                                                )
                                            ))

                                            setShowCancelWarning(false)
                                        }} className={'btn btn-primary w-100'}>
                                            {strings.app?.text.yes}
                                        </button>

                                    </div>
                                </div>
                            </ModalBody>
                        </Modal>
                    </div>
                </div>

            </div>

        </div>

        <div
            className={'position-fixed bottom-0 end-0 pe-5 fs-label fw-light version-text'}
        >
            {MODULE_VERSION}
        </div>
    </div>
}

export default CarepathDetailsInterface
